// @ts-nocheck
import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Form,
  Alert,
  Tooltip,
  OverlayTrigger,
  Collapse,
  Badge,
} from "react-bootstrap";
import useUser from "../hooks/useUser";
import SavedEnvVarSetSelector from "../components/SavedEnvVarSetSelector";
import StrategyCompatibility from "../components/StrategyCompatibility/StrategyCompatibility";
import BrokerConnectionSelector from "../components/BrokerConnectionSelector";
import { brokerConnectionNeedsAttention } from "../utils/brokerConnectionStatus";
import { pushEvent, ANALYTICS_EVENTS } from "../utils/analytics";
import "./DeployPage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { fetchWithAuth } from "../utils/apiHelpers";
import fetchWithTimeout from "../utils/fetchWithTimeout";
import {
  SAVED_ENV_VAR_SET_SELECTION_KEYS,
  loadSavedEnvVarSetSelection,
  saveSavedEnvVarSetSelection,
} from "../utils/savedEnvVarSetSelection";
import {
  getRuntimePreference,
  updateRuntimePreference,
} from "../utils/savedSecrets";
import {
  evaluateRuntimeSecretRequirements,
  resolveCompatibilitySetupWithAvailableKeys,
  runtimeSecretRequirementItems,
} from "../utils/runtimeSecretRequirements";
import BacktestResults from "../components/BacktestResults";
import PaywallModal from "../components/UnifiedModal/variants/PaywallModal";
import { publishStrategy } from "./MarketplaceStrategy/MarketplaceAPI";
import {
  Alert as BsuiAlert,
  Button as BsuiButton,
  PageHeader,
  PageShell,
  SegmentedControl,
  Spinner as BsuiSpinner,
} from "../design-system";

const DEPLOY_PAGE_FETCH_TIMEOUT_MS = 15000;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const BROKER_SECRET_ENV_ALIASES = {
  alpaca: [
    "ALPACA_API_KEY",
    "ALPACA_API_SECRET",
    "ALPACA_SECRET_KEY",
    "ALPACA_OAUTH_TOKEN",
  ],
  schwab: ["SCHWAB_TOKEN", "SCHWAB_ACCOUNT_NUMBER"],
  charles_schwab: ["SCHWAB_TOKEN", "SCHWAB_ACCOUNT_NUMBER"],
  tradier: ["TRADIER_ACCESS_TOKEN", "TRADIER_TOKEN", "TRADIER_ACCOUNT_NUMBER"],
};

const DEPLOY_MODE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "always_on", label: "Always on" },
  { value: "scheduled", label: "Scheduled" },
];

const SCHEDULE_FREQUENCY_OPTIONS = [
  { value: "5m", label: "Every 5 minutes", intervalSeconds: 300 },
  { value: "15m", label: "Every 15 minutes", intervalSeconds: 900 },
  { value: "30m", label: "Every 30 minutes", intervalSeconds: 1800 },
  { value: "1h", label: "Hourly", intervalSeconds: 3600 },
  { value: "1d", label: "Daily", intervalSeconds: 86400 },
];

const getScheduleFrequencyOption = (value) =>
  SCHEDULE_FREQUENCY_OPTIONS.find((option) => option.value === value) ||
  SCHEDULE_FREQUENCY_OPTIONS[1];

const formatDeployModeSummary = (mode, frequencyValue) => {
  if (mode === "always_on") return "Always on";
  if (mode === "scheduled")
    return `Scheduled - ${getScheduleFrequencyOption(frequencyValue).label.toLowerCase()}`;
  return "Auto";
};

const buildIntervalScheduleMetadata = (
  frequencyValue,
  deploymentMode = "scheduled",
) => {
  const option = getScheduleFrequencyOption(frequencyValue);
  return {
    schemaVersion: 2,
    deploymentMode,
    cadence: {
      type: "interval",
      intervalSeconds: option.intervalSeconds,
    },
    marketSession: "regular",
    requiresAlwaysOn: false,
    liveCadenceMatchesBacktest: true,
    source: "manual_override",
  };
};

const normalizeRevisionScheduleMetadata = (revision) => {
  const metadata =
    revision?.scheduleMetadata || revision?.schedule_metadata || null;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata))
    return null;
  return metadata;
};

const normalizeBrokerKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getBrokerCredentialSatisfiedKeys = (credential, fallbackBroker) => {
  if (!credential?.id) return [];
  const brokerKey = normalizeBrokerKey(credential.broker || fallbackBroker);
  const keyNames = Array.isArray(credential.keyNames)
    ? credential.keyNames
    : [];
  const aliases = BROKER_SECRET_ENV_ALIASES[brokerKey] || [];
  return Array.from(new Set([...keyNames, ...aliases].filter(Boolean)));
};

export default function DeployPage() {
  const location = useLocation();
  const query = useQuery();
  const strategyId = query.get("strategyId");
  const aiStrategyId = query.get("aiStrategyId");
  const aiStrategyRevisionId = query.get("aiStrategyRevisionId");
  // LinkedStrategy support - for protected strategies where code stays with author
  const linkedStrategyId = query.get("linkedStrategyId");
  const navigate = useNavigate();
  const {
    isAuthenticated,
    loginWithRedirect,
    isAdmin: isUserAdmin,
    isBetaTester,
    isLoading: userLoading,
    getApiToken,
    userProfile,
  } = useUser();

  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  // Load saved broker from localStorage, default to "alpaca" if none saved
  const [broker, setBroker] = useState(() => {
    const savedBroker = localStorage.getItem("preferred_broker");
    return savedBroker || "alpaca";
  });

  // Paper trading flag (shared) - Load from localStorage but default to paper for safety
  const [isPaper, setIsPaper] = useState(() => {
    const savedMode = localStorage.getItem("preferred_trading_mode");
    // Default to paper (true) for safety, only go to live if explicitly saved and user has permission
    return savedMode === "live" ? false : true;
  });
  const [canUseLive, setCanUseLive] = useState(false);
  const [agreeTos, setAgreeTos] = useState(false);
  const [agreeBeta, setAgreeBeta] = useState(false);
  const [flashAgreements, setFlashAgreements] = useState(false);
  const [publishToMarketplace, setPublishToMarketplace] = useState(false);
  const [marketplacePricingModel, setMarketplacePricingModel] =
    useState("free");
  const [marketplacePriceAmount, setMarketplacePriceAmount] = useState("");
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState("");

  // Paywall modal state for limit exceeded scenarios
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [paywallData, setPaywallData] = useState(null);
  const [mainPy, setMainPy] = useState(""); // This holds the strategy code
  const [requirementsTxt, setRequirementsTxt] = useState("");
  const [strategyCodeError, setStrategyCodeError] = useState("");
  const [savedEnvVarSetIds, setSavedEnvVarSetIds] = useState(() =>
    loadSavedEnvVarSetSelection(SAVED_ENV_VAR_SET_SELECTION_KEYS.deployment),
  );
  const [savedEnvVarSets, setSavedEnvVarSets] = useState([]);
  const [selectedBrokerCredentialId, setSelectedBrokerCredentialId] =
    useState("");
  const [selectedBrokerCredential, setSelectedBrokerCredential] =
    useState(null);
  const [deployMode, setDeployMode] = useState("auto");
  const [deployModeExpanded, setDeployModeExpanded] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState("15m");
  const [revisionScheduleMetadata, setRevisionScheduleMetadata] =
    useState(null);
  const [revisionVersion, setRevisionVersion] = useState(null);
  const [revisionOptions, setRevisionOptions] = useState([]);
  const [runtimeSecretRequirements, setRuntimeSecretRequirements] =
    useState(null);
  const [runtimeSecretSetupNotes, setRuntimeSecretSetupNotes] = useState({});
  const [compatibilityAssessment, setCompatibilityAssessment] = useState(null);
  const [deployId, setDeployId] = useState(null);
  const [deployStatus, setDeployStatus] = useState(null);
  const [deployStatusError, setDeployStatusError] = useState("");
  const [performanceData, setPerformanceData] = useState(null); // Currently not used - endpoint doesn't exist yet

  const [requiredVarsStatus, setRequiredVarsStatus] = useState({
    required: [],
    missing: [],
    present: [],
    invalidLengthDetails: [],
  });
  const [showPerformance, setShowPerformance] = useState(true);

  const buildDeploymentAnalyticsPayload = useCallback(
    (additional = {}) => {
      const base = {
        strategyId,
        strategy_id: strategyId,
        aiStrategyRevisionId,
        ai_strategy_revision_id: aiStrategyRevisionId,
        broker: selectedBrokerCredential?.broker || broker,
        brokerCredentialId: selectedBrokerCredentialId || null,
        tradingMode:
          selectedBrokerCredential?.paperOrLive || (isPaper ? "paper" : "live"),
        trading_mode:
          selectedBrokerCredential?.paperOrLive || (isPaper ? "paper" : "live"),
        isPaper: selectedBrokerCredential?.paperOrLive
          ? selectedBrokerCredential.paperOrLive === "paper"
          : isPaper,
        is_paper: isPaper,
        isLive: !isPaper,
        is_live: !isPaper,
        publishToMarketplace,
        publish_to_marketplace: publishToMarketplace,
        deploymentMode: deployMode,
        deployment_mode: deployMode,
        event_source: "frontend",
      };

      const combined = { ...base, ...additional };

      const keyMappings = [
        ["deployId", "deployment_id"],
        ["deploymentId", "deployment_id"],
        ["botId", "bot_id"],
        ["managerBotId", "manager_bot_id"],
        ["errorCode", "error_code"],
        ["errorMessage", "error_message"],
        ["failureStage", "failure_stage"],
        ["failureReason", "failure_reason"],
        ["status", "status"],
        ["durationMs", "duration_ms"],
        ["durationSeconds", "duration_seconds"],
        ["authMethod", "auth_method"],
        ["uiSource", "ui_source"],
        ["provider", "provider"],
        ["hasCustomEnvVars", "has_custom_env_vars"],
      ];

      keyMappings.forEach(([camel, snake]) => {
        if (
          Object.prototype.hasOwnProperty.call(combined, camel) &&
          combined[snake] === undefined
        ) {
          combined[snake] = combined[camel];
        }
      });

      if (
        combined.duration_ms === undefined &&
        combined.durationSeconds !== undefined
      ) {
        combined.duration_ms = Math.round(
          Number(combined.durationSeconds) * 1000,
        );
      }

      if (
        combined.durationSeconds === undefined &&
        combined.duration_ms !== undefined
      ) {
        combined.durationSeconds = Math.round(
          Number(combined.duration_ms) / 1000,
        );
        combined.duration_seconds = combined.durationSeconds;
      }

      if (!combined.timestamp) {
        combined.timestamp = new Date().toISOString();
      }

      return combined;
    },
    [
      strategyId,
      aiStrategyRevisionId,
      broker,
      isPaper,
      publishToMarketplace,
      deployMode,
    ],
  );

  const emitDeploymentEvent = useCallback(
    (eventConst, additional = {}) => {
      pushEvent({
        event: eventConst,
        ...buildDeploymentAnalyticsPayload(additional),
      });
    },
    [buildDeploymentAnalyticsPayload],
  );

  useEffect(() => {
    saveSavedEnvVarSetSelection(
      SAVED_ENV_VAR_SET_SELECTION_KEYS.deployment,
      savedEnvVarSetIds,
    );
  }, [savedEnvVarSetIds]);

  const selectedSavedEnvVarKeyNames = React.useMemo(() => {
    const selected = new Set(savedEnvVarSetIds || []);
    return Array.from(
      new Set(
        savedEnvVarSets
          .filter((item) => selected.has(item.id))
          .flatMap((item) =>
            Array.isArray(item.keyNames) ? item.keyNames : [],
          )
          .filter(Boolean),
      ),
    );
  }, [savedEnvVarSetIds, savedEnvVarSets]);

  const selectedBrokerCredentialKeyNames = React.useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(selectedBrokerCredential?.keyNames)
            ? selectedBrokerCredential.keyNames
            : []
          ).filter(Boolean),
        ),
      ),
    [selectedBrokerCredential],
  );

  const brokerCredentialSatisfiedKeyNames = React.useMemo(
    () => getBrokerCredentialSatisfiedKeys(selectedBrokerCredential, broker),
    [selectedBrokerCredential, broker],
  );

  const availableRuntimeSecretKeys = React.useMemo(
    () => Array.from(new Set([
      ...selectedSavedEnvVarKeyNames,
      ...selectedBrokerCredentialKeyNames,
      ...brokerCredentialSatisfiedKeyNames,
    ])),
    [
      selectedSavedEnvVarKeyNames,
      selectedBrokerCredentialKeyNames,
      brokerCredentialSatisfiedKeyNames,
    ],
  );

  const runtimePreferenceKey = React.useMemo(
    () => ({
      strategyId: strategyId || null,
      aiStrategyRevisionId: aiStrategyRevisionId || null,
      linkedStrategyId: linkedStrategyId || null,
    }),
    [strategyId, aiStrategyRevisionId, linkedStrategyId],
  );

  const selectedBrokerIsLive = selectedBrokerCredential?.paperOrLive === "live";
  const liveStartRequested = selectedBrokerIsLive || (canUseLive && !isPaper);

  const startButtonLabel = liveStartRequested
    ? "Start live bot"
    : "Start paper bot";

  const saveRuntimePreference = useCallback(
    async (tokenOverride) => {
      if (
        !getApiToken ||
        (!runtimePreferenceKey.strategyId &&
          !runtimePreferenceKey.linkedStrategyId)
      ) {
        return;
      }
      try {
        const token = tokenOverride || (await getApiToken());
        await updateRuntimePreference(token, {
          ...runtimePreferenceKey,
          brokerCredentialId: selectedBrokerCredentialId || null,
          envVarSetIds: savedEnvVarSetIds,
        });
      } catch (error) {
        console.warn("[DeployPage] Failed to save start defaults", error);
      }
    },
    [
      getApiToken,
      runtimePreferenceKey,
      selectedBrokerCredentialId,
      savedEnvVarSetIds,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    const loadRuntimePreference = async () => {
      if (!isAuthenticated || userLoading || !getApiToken) return;
      if (
        !runtimePreferenceKey.strategyId &&
        !runtimePreferenceKey.linkedStrategyId
      )
        return;
      try {
        const token = await getApiToken();
        const preference = await getRuntimePreference(
          token,
          runtimePreferenceKey,
        );
        if (cancelled || !preference) return;
        if (preference.brokerCredentialId) {
          setSelectedBrokerCredentialId(preference.brokerCredentialId);
        }
        if (Array.isArray(preference.envVarSetIds)) {
          setSavedEnvVarSetIds(preference.envVarSetIds);
        }
      } catch (error) {
        console.warn("[DeployPage] Failed to load start defaults", error);
      }
    };
    loadRuntimePreference();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userLoading, getApiToken, runtimePreferenceKey]);

  useEffect(() => {
    const runtimeStatus = evaluateRuntimeSecretRequirements(
      runtimeSecretRequirements,
      availableRuntimeSecretKeys,
      "deploy",
    );
    const runtimeMissingDetails = runtimeStatus.missing.map((item) => ({
      key: item.key,
      message: item.message,
    }));
    const runtimeMissingKeys = runtimeStatus.missing.map((item) => item.key);
    setRequiredVarsStatus({
      required: runtimeStatus.required,
      present: runtimeStatus.present,
      missing: runtimeMissingKeys,
      invalidLengthDetails: runtimeMissingDetails,
      runtimeMissingDetails,
    });
  }, [
    runtimeSecretRequirements,
    availableRuntimeSecretKeys,
  ]);

  // Enable live toggle for admins and beta testers only
  useEffect(() => {
    const hasLiveAccess = true;
    setCanUseLive(hasLiveAccess);

    // If user has live access and previously selected live mode, restore it
    if (hasLiveAccess) {
      const savedMode = localStorage.getItem("preferred_trading_mode");
      if (savedMode === "live") {
        setIsPaper(false);
      }
    }
  }, [isUserAdmin, isBetaTester]);

  // If user lacks live access, always fall back to paper mode for safety
  useEffect(() => {
    if (!canUseLive && !isPaper && broker !== "schwab") {
      setIsPaper(true);
    }
  }, [canUseLive, isPaper, broker]);

  // Schwab is live-only: force live mode in the UI.
  useEffect(() => {
    if (broker === "schwab" && isPaper) {
      setIsPaper(false);
    }
  }, [broker, isPaper]);

  // Save broker preference to localStorage whenever it changes
  useEffect(() => {
    if (broker) {
      localStorage.setItem("preferred_broker", broker);
    }
  }, [broker]);

  // Save trading mode preference to localStorage whenever it changes
  useEffect(() => {
    // Only save if user has permission to use live trading
    // Otherwise always save as paper to prevent unauthorized live trading on reload
    const modeToSave = !isPaper && canUseLive ? "live" : "paper";
    localStorage.setItem("preferred_trading_mode", modeToSave);
  }, [isPaper, canUseLive]);

  const fetchStrategyCodeAndRequirements = useCallback(
    async (strategyId, aiStrategyRevisionId) => {
      try {
        // Check if this is a public strategy from URL parameters
        const isPublicStrategy = query.get("isPublic") === "true";

        const token = await getApiToken();
        const strategyDataPromise = (async () => {
          if (isPublicStrategy) {
            // For public strategies, use the public endpoint without authentication
            const response = await fetchWithTimeout(
              `${process.env.REACT_APP_API_URL}/strategies/public/${strategyId}`,
              {},
              DEPLOY_PAGE_FETCH_TIMEOUT_MS,
            );
            if (!response.ok) {
              throw new Error(
                `Failed to fetch public strategy: ${response.status} ${response.statusText}`,
              );
            }
            return response.json();
          }
          // For private strategies, use the authenticated endpoint
          return fetchWithAuth(token, `/strategies/${strategyId}`);
        })();

        // Prefer the URL aiStrategyId. Internal deploy links already carry it, and using it
        // avoids fetching the full AI strategy library before the Start button becomes usable.
        let strategyData = null;
        let targetAiStrategyId = aiStrategyId;

        if (!targetAiStrategyId && isPublicStrategy) {
          strategyData = await strategyDataPromise;
          targetAiStrategyId =
            strategyData.aiStrategyId ||
            strategyData.ai_strategy_id ||
            strategyData.aiStrategy?.id ||
            null;
        }

        if (!targetAiStrategyId && !isPublicStrategy) {
          // For private strategies, find the AI strategy in user's list
          const [resolvedStrategyData, aiStrategiesRes] = await Promise.all([
            strategyDataPromise,
            fetchWithAuth(token, `/ai-bot-builder/list-strategies`),
          ]);
          strategyData = resolvedStrategyData;
          const aiStrategies = aiStrategiesRes.aiStrategies || [];
          const aiStrategy = aiStrategies.find(
            (s) => s.strategy && s.strategy.id === strategyId,
          );

          if (!aiStrategy)
            throw new Error("Could not find AI strategy for this strategy");
          targetAiStrategyId = aiStrategy.id;
        }

        if (aiStrategyRevisionId) {
          if (!targetAiStrategyId) {
            throw new Error(
              "Could not determine AI strategy for this deployment.",
            );
          }
          const versionsPromise = fetchWithAuth(
            token,
            `/ai-bot-builder/list-versions?aiStrategyId=${encodeURIComponent(targetAiStrategyId)}`,
          );
          const [resolvedStrategyData, versionsRes] = strategyData
            ? [strategyData, await versionsPromise]
            : await Promise.all([strategyDataPromise, versionsPromise]);
          setStrategy(resolvedStrategyData);
          const versionsData = versionsRes;
          const versions = Array.isArray(versionsData.versions)
            ? versionsData.versions
            : [];
          setRevisionOptions(versions);
          const revision = versions.find(
            (v) => v.revisionId === aiStrategyRevisionId,
          );

          if (revision && (revision.main_py || revision.code_out)) {
            const code = revision.main_py || revision.code_out || ""; // Prefer main_py, fallback to code_out
            setMainPy(code); // Set the strategy code here
            setRequirementsTxt(revision.requirements_txt || "lumibot"); // Default requirements
            setStrategyCodeError("");
            setRevisionVersion(revision.version || null);
            setRuntimeSecretRequirements(
              revision.runtimeSecretRequirements || null,
            );
            setRuntimeSecretSetupNotes(revision.runtimeSecretSetupNotes || {});
            setCompatibilityAssessment(revision.compatibilityAssessment || null);
            setRevisionScheduleMetadata(
              normalizeRevisionScheduleMetadata(revision),
            );
          } else {
            setMainPy("");
            setRequirementsTxt("");
            setStrategyCodeError("No code found for this revision.");
            setRevisionVersion(null);
            setRuntimeSecretRequirements(null);
            setRuntimeSecretSetupNotes({});
            setCompatibilityAssessment(null);
            setRevisionScheduleMetadata(null);
            setRevisionOptions(versions);
            console.warn(
              "[DeployPage] No code found for revision:",
              aiStrategyRevisionId,
            );
          }
        } else {
          strategyData = strategyData || (await strategyDataPromise);
          setStrategy(strategyData);
          setMainPy("");
          setRequirementsTxt("");
          setStrategyCodeError("No revision selected.");
          setRevisionVersion(null);
          setRuntimeSecretRequirements(null);
          setRuntimeSecretSetupNotes({});
          setCompatibilityAssessment(null);
          setRevisionScheduleMetadata(null);
          setRevisionOptions([]);
          console.warn("[DeployPage] No revision ID provided.");
        }
      } catch (e) {
        console.error("[DeployPage] Error fetching strategy/code:", e);
        setStrategy(null);
        setMainPy("");
        setRequirementsTxt("");
        setStrategyCodeError(
          "Error fetching strategy code: " + (e.message || e),
        );
        setRevisionVersion(null);
        setRuntimeSecretRequirements(null);
        setRuntimeSecretSetupNotes({});
        setCompatibilityAssessment(null);
        setRevisionScheduleMetadata(null);
        setRevisionOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [getApiToken, query, aiStrategyId],
  );

  // Fetch linked strategy info for protected strategies
  const fetchLinkedStrategyInfo = useCallback(
    async (linkedId) => {
      try {
        const token = await getApiToken();
        const response = await fetchWithTimeout(
          `${process.env.REACT_APP_API_URL}/linked-strategies/${linkedId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
          DEPLOY_PAGE_FETCH_TIMEOUT_MS,
        );

        if (!response.ok) {
          // Handle expired marketplace purchase — redirect to strategy detail page
          if (response.status === 403) {
            try {
              const errData = await response.json();
              if (errData.code === "PURCHASE_REQUIRED" && errData.listingId) {
                navigate(`/marketplace/strategy/${errData.listingId}`);
                return;
              }
            } catch {
              /* fall through to generic error */
            }
          }
          throw new Error(
            `Failed to fetch linked strategy: ${response.status}`,
          );
        }

        const linkedData = await response.json();

        // Set strategy info from linked strategy
        setStrategy({
          id: linkedData.originalStrategy?.id,
          name: linkedData.name || linkedData.originalStrategy?.name,
          isLinked: true,
          linkedStrategyId: linkedId,
        });

        // For linked strategies, code is loaded by backend during deploy
        // We don't have access to view it, so show a placeholder message
        setMainPy("// Protected strategy - code is managed by the author");
        setRequirementsTxt("lumibot");
        setStrategyCodeError("");
        setRevisionVersion(linkedData.latestRevision?.version || null);
        setRevisionOptions(
          linkedData.latestRevision ? [linkedData.latestRevision] : [],
        );
        setRuntimeSecretRequirements(
          linkedData.latestRevision?.runtimeSecretRequirements || null,
        );
        setRuntimeSecretSetupNotes(
          linkedData.latestRevision?.runtimeSecretSetupNotes || {},
        );
        setCompatibilityAssessment(
          linkedData.latestRevision?.compatibilityAssessment || null,
        );
        setRevisionScheduleMetadata(
          normalizeRevisionScheduleMetadata(linkedData.latestRevision),
        );
      } catch (e) {
        console.error("[DeployPage] Error fetching linked strategy:", e);
        setStrategy(null);
        setMainPy("");
        setRequirementsTxt("");
        setStrategyCodeError(
          "Error fetching linked strategy: " + (e.message || e),
        );
        setRevisionVersion(null);
        setRuntimeSecretRequirements(null);
        setCompatibilityAssessment(null);
        setRevisionScheduleMetadata(null);
        setRevisionOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [getApiToken],
  );

  // Fetch strategy info and code on mount or param change
  useEffect(() => {
    setLoading(true); // Set loading true at the start
    if (linkedStrategyId) {
      // LinkedStrategy - fetch info from linked-strategies endpoint
      fetchLinkedStrategyInfo(linkedStrategyId);
    } else if (strategyId && aiStrategyRevisionId) {
      // Ensure both IDs are present
      fetchStrategyCodeAndRequirements(strategyId, aiStrategyRevisionId);
    } else {
      setLoading(false); // Set loading false if IDs are missing
      setStrategyCodeError(
        "Missing strategyId or aiStrategyRevisionId in query parameters.",
      );
      console.error("[DeployPage] Missing required query parameters.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyId, aiStrategyRevisionId, linkedStrategyId]); // Added linkedStrategyId to deps

  const handleDeploy = async (e) => {
    e.preventDefault();

    let accumulatedErrors = "";

    if (!selectedBrokerCredentialId) {
      accumulatedErrors += "Choose or add a broker before starting this bot. ";
    }
    if (brokerConnectionNeedsAttention(selectedBrokerCredential)) {
      accumulatedErrors += "Reconnect this broker before starting this bot. ";
    }

    // 1. Validate required environment variables against selected saved env-var sets.
    const deployMissingRequiredVars = Array.isArray(requiredVarsStatus?.missing)
      ? requiredVarsStatus.missing
      : [];
    const deployInvalidLengthDetails = Array.isArray(
      requiredVarsStatus?.invalidLengthDetails,
    )
      ? requiredVarsStatus.invalidLengthDetails
      : [];
    if (deployMissingRequiredVars.length > 0) {
      const problemKeys = deployMissingRequiredVars;
      let errorMessages = problemKeys.map((key) => {
        const detail = deployInvalidLengthDetails.find((d) => d.key === key);
        return detail ? detail.message : `${key} is missing a value`;
      });
      accumulatedErrors += `Please resolve environment variable issues: ${errorMessages.join("; ")}. `;
    }

    // 3. Validate Strategy Code Presence
    if (!mainPy || !requirementsTxt) {
      accumulatedErrors +=
        strategyCodeError || "Strategy code or requirements are missing. ";
    }

    let publishPriceAmountCents = null;
    if (publishToMarketplace && marketplacePricingModel === "monthly") {
      const priceNumber = Number(marketplacePriceAmount);
      publishPriceAmountCents = Math.round(priceNumber * 100);
      if (
        !Number.isFinite(priceNumber) ||
        !Number.isInteger(publishPriceAmountCents) ||
        publishPriceAmountCents < 100 ||
        publishPriceAmountCents > 1000000
      ) {
        accumulatedErrors +=
          "Marketplace price must be between $1.00 and $10,000.00. ";
      }
    }

    // If any errors were accumulated, set deployError and return
    if (accumulatedErrors) {
      setDeployError(accumulatedErrors.trim());
      const errorSection =
        document.querySelector(".deploy-message-container") ||
        document.querySelector(".saved-env-var-set-selector");
      errorSection?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    // Agreements check when live requested
    if (liveStartRequested) {
      if (!agreeTos || !agreeBeta) {
        setDeployError(
          "Accept the terms and live trading risk note before starting a live bot.",
        );
        return;
      }
    }

    // Clear any previous error if all validations pass
    setDeployError("");
    setDeploying(true);
    setDeployId(null);
    const requestStartedAt = Date.now();

    emitDeploymentEvent(ANALYTICS_EVENTS.DEPLOY_REQUEST_INITIATED, {
      status: "initiated",
      authMethod: selectedBrokerCredential?.authMethod || null,
      hasCustomEnvVars: false,
      savedEnvVarSetCount: savedEnvVarSetIds.length,
    });

    try {
      const token = await getApiToken();

      // Build the request body
      const deployRequestBody = {
        strategyId,
        strategy_id: strategyId,
        aiStrategyId,
        ai_strategy_id: aiStrategyId,
        aiStrategyRevisionId,
        revisionId: aiStrategyRevisionId,
        revision_id: aiStrategyRevisionId,
        brokerCredentialId: selectedBrokerCredentialId,
        deploymentMode: deployMode,
        ...(savedEnvVarSetIds.length > 0 && {
          envVarSetIds: savedEnvVarSetIds,
        }),
        // LinkedStrategy support - for protected strategies where code stays with author
        ...(linkedStrategyId && { linkedStrategyId }),
      };
      if (deployMode === "scheduled") {
        deployRequestBody.scheduleMetadata = buildIntervalScheduleMetadata(
          scheduleFrequency,
          "scheduled",
        );
      } else if (deployMode === "auto" && revisionScheduleMetadata) {
        deployRequestBody.scheduleMetadata = revisionScheduleMetadata;
      }

      // Add acceptances for live trading
      if (liveStartRequested) {
        deployRequestBody.acceptances = {
          acceptedTerms: agreeTos,
          acceptedBetaRisk: agreeBeta,
        };
      }

      const data = await fetchWithAuth(token, "/deploy", {
        method: "POST",
        body: deployRequestBody,
      });

      setDeployError("");
      setDeployId(data.deployId);
      void saveRuntimePreference(token);

      let publishResult = null;
      if (publishToMarketplace && strategyId && !linkedStrategyId) {
        try {
          publishResult = await publishStrategy(
            strategyId,
            {
              pricingModel: marketplacePricingModel,
              ...(marketplacePricingModel === "monthly"
                ? { priceAmountCents: publishPriceAmountCents }
                : {}),
              priceCurrency: "usd",
              protectCode:
                marketplacePricingModel === "monthly" ? true : undefined,
            },
            token,
          );
          const moderationStatus = publishResult?.moderationStatus || "approved";
          pushEvent({
            event: "marketplace_publish_on_start_success",
            strategyId,
            listingId: publishResult?.listingId || publishResult?.id || null,
            moderationStatus,
            pricingModel:
              publishResult?.pricingModel || marketplacePricingModel,
            priceAmountCents:
              publishResult?.priceAmountCents || publishPriceAmountCents,
            source: "deploy_page",
          });
        } catch (publishErr) {
          console.error(
            "[DeployPage] Bot started, but Marketplace publish failed:",
            publishErr,
          );
          pushEvent({
            event: "marketplace_publish_on_start_failed",
            strategyId,
            pricingModel: marketplacePricingModel,
            errorMessage: publishErr?.message || String(publishErr),
            source: "deploy_page",
          });
        }
      }

      emitDeploymentEvent(ANALYTICS_EVENTS.DEPLOY_REQUEST_SUCCESS, {
        deployId: data.deployId,
        botId: data.lumiwealth_bot_id,
        managerBotId: data.manager_bot_id || null,
        status: "in_progress",
        durationMs: Date.now() - requestStartedAt,
      });

      const submittedListingId =
        publishResult?.listingId || publishResult?.id || null;
      if (data.lumiwealth_bot_id) {
        const botParams = new URLSearchParams();
        if (data.deployId) {
          botParams.set("deployment", data.deployId);
        }
        if (submittedListingId) {
          botParams.set("marketplace", "published");
          botParams.set("listing", submittedListingId);
        } else if (publishToMarketplace && strategyId && !linkedStrategyId) {
          botParams.set("marketplace", "publish_failed");
        }
        const botQuery = botParams.toString();
        navigate(
          `/bot/${encodeURIComponent(data.lumiwealth_bot_id)}${botQuery ? `?${botQuery}` : ""}`,
        );
      } else {
        navigate(`/deployments/${encodeURIComponent(data.deployId)}`);
      }
      // Keep deploying=true because we are navigating away
    } catch (err) {
      console.error("[DeployPage] Failed to deploy bot:", err);
      // Try to get message from err.data first, then err.message, then default
      let errorMsg =
        "An unknown error occurred during deployment. Please check configuration or contact support.";
      if (err?.data?.message) {
        errorMsg = err.data.message;
      } else if (err?.message) {
        // Handle the specific "true" message from fetchWithAuth if needed
        if (err.message === "true") {
          errorMsg =
            "Deployment request failed. Check browser console for API error details (e.g., account limits).";
          console.warn(
            "[DeployPage] Received 'true' error, API details should be in console logs from apiHelpers.",
          );
        } else {
          errorMsg = err.message;
        }
      }
      // Handle specific known error codes if available (less likely with current fetchWithAuth)
      const errorCode = err?.data?.errorCode || err?.errorCode;

      // Show PaywallModal for MAX_BOTS_REACHED instead of just an error message
      if (errorCode === "MAX_BOTS_REACHED") {
        const usage = err?.data?.usage || { used: 0, limit: 0 };
        // Pass through active deployments so the modal can show the user which
        // bots are already running. Without this, a paid-tier user sees a pitch
        // to buy the plan they already have.
        const activeDeployments = err?.data?.activeDeployments || [];
        setPaywallData({
          limitType: "bot",
          usage: {
            used: usage.used,
            limit: usage.limit,
            remaining: 0,
            resetsAt: usage.resetsAt,
          },
          activeDeployments,
          checkoutUrl: "/checkout/starter-plan",
        });
        setShowPaywallModal(true);
        setDeploying(false);

        emitDeploymentEvent(ANALYTICS_EVENTS.DEPLOY_REQUEST_FAILED, {
          status: "failed",
          errorMessage: "Bot limit reached",
          errorCode: "MAX_BOTS_REACHED",
          failureStage: "limit_check",
          durationMs: Date.now() - requestStartedAt,
        });
        return; // Don't show error toast, paywall modal handles it
      }

      setDeployError(errorMsg);
      emitDeploymentEvent(ANALYTICS_EVENTS.DEPLOY_REQUEST_FAILED, {
        status: "failed",
        errorMessage: errorMsg,
        errorCode:
          errorCode ||
          (err?.message === "true" ? "UNKNOWN_FETCHWITHAUTH_ERROR" : null),
        failureStage: "start",
        durationMs: Date.now() - requestStartedAt,
      });
      setDeploying(false); // Set deploying false only on error
    }
  };

  // Helper to determine if deployment is still in progress (button should be loading)
  // Simplified as we only care about the initial request now
  const isDeployingState = deploying;

  // Update back button navigation
  const handleBack = () => {
    // Navigate back to the AI Bot Builder page, including the aiStrategyId if available
    if (aiStrategyId) {
      navigate(`/ai-bot-builder/${aiStrategyId}`);
    } else {
      // Fallback if aiStrategyId is somehow missing
      navigate("/ai-bot-builder");
    }
  };

  const renderTradingModeTooltip = (props) => (
    <Tooltip id="trading-mode-tooltip" {...props}>
      {canUseLive
        ? "Toggle between Paper Trading (virtual money) and Live Trading (real money). Live trading requires additional agreements."
        : "Live trading uses real money. Paper trading uses virtual money for safe testing."}
    </Tooltip>
  );

  const liveOnlyBrokerRequiresAccess =
    Boolean(
      selectedBrokerCredential?.broker === "schwab" ||
      selectedBrokerCredential?.paperOrLive === "live",
    ) && !canUseLive;

  const selectedBrokerNeedsReconnect = brokerConnectionNeedsAttention(
    selectedBrokerCredential,
  );

  const showLiveTradingRequirements =
    liveStartRequested && !liveOnlyBrokerRequiresAccess;

  const liveTermsMissing =
    showLiveTradingRequirements && (!agreeTos || !agreeBeta);

  const missingRequiredVars = Array.isArray(requiredVarsStatus?.missing)
    ? requiredVarsStatus.missing
    : [];
  const invalidLengthDetails = Array.isArray(
    requiredVarsStatus?.invalidLengthDetails,
  )
    ? requiredVarsStatus.invalidLengthDetails
    : [];
  const runtimeMissingDetails = Array.isArray(
    requiredVarsStatus?.runtimeMissingDetails,
  )
    ? requiredVarsStatus.runtimeMissingDetails
    : [];
  const compatibilityAction = liveStartRequested ? 'live' : 'paper';
  const effectiveCompatibilityAssessment = React.useMemo(
    () => resolveCompatibilitySetupWithAvailableKeys(
      compatibilityAssessment,
      availableRuntimeSecretKeys,
      compatibilityAction,
    ),
    [compatibilityAssessment, availableRuntimeSecretKeys, compatibilityAction],
  );
  const compatibilityBlockers = Array.isArray(effectiveCompatibilityAssessment?.requirements)
    ? effectiveCompatibilityAssessment.requirements.filter((requirement) => {
        const status = requirement?.statusByAction?.[compatibilityAction]?.status;
        return requirement?.essential === true &&
          (status === 'unavailable' || status === 'setup_required');
      })
    : [];

  const isDeployDisabled =
    deploying ||
    !selectedBrokerCredentialId ||
    selectedBrokerNeedsReconnect ||
    missingRequiredVars.length > 0 ||
    compatibilityBlockers.length > 0 ||
    liveTermsMissing ||
    liveOnlyBrokerRequiresAccess;

  const startButtonBlocked = isDeployDisabled && !deploying;
  const startButtonDisabledReason = !selectedBrokerCredentialId
    ? "Choose or add a broker before starting this bot."
    : selectedBrokerNeedsReconnect
      ? "Reconnect this broker before starting this bot."
      : missingRequiredVars.length > 0
        ? `Add saved API keys for: ${missingRequiredVars
            .map((key) => {
              const detail = invalidLengthDetails.find((d) => d.key === key);
              return detail ? detail.message : `${key} (missing value)`;
            })
            .join("; ")}`
        : compatibilityBlockers.length > 0
          ? compatibilityBlockers
              .map((item) => item?.statusByAction?.[compatibilityAction]?.reason)
              .filter(Boolean)
              .join('; ')
        : liveOnlyBrokerRequiresAccess
          ? "Live trading access is required for this broker. Email support@lumiwealth.com to enable it."
          : liveTermsMissing
            ? "Accept the terms and live trading risk note before starting this bot."
            : "Check the missing fields before starting this bot.";

  // Handle deploy button click when disabled
  const handleDeployButtonClick = (event) => {
    if (startButtonBlocked) {
      event?.preventDefault();
      // If it's disabled due to terms acceptance, flash the agreements
      if (!liveOnlyBrokerRequiresAccess && liveTermsMissing) {
        setFlashAgreements(true);
        setTimeout(() => setFlashAgreements(false), 1000);
        return;
      }
    }
    // If not disabled, trigger analytics
    if (!isDeployDisabled) {
      emitDeploymentEvent(ANALYTICS_EVENTS.DEPLOY_BUTTON_CLICKED);
    }
  };

  const handleRevisionChange = (event) => {
    const nextRevisionId = event.target.value;
    if (!nextRevisionId || nextRevisionId === aiStrategyRevisionId) return;
    const params = new URLSearchParams(location.search || "");
    params.set("aiStrategyRevisionId", nextRevisionId);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  return (
    <PageShell
      className="deploy-page"
      innerClassName="deploy-container deploy-page-full-width"
    >
      <PageHeader
        className="strategy-header start-bot-header"
        meta="Deployment"
        title="Start bot"
        description="Pick where it trades, then start it."
      />
      {loading ? (
        <div className="deploy-page-loading">
          <BsuiSpinner />
        </div>
      ) : !strategy || strategyCodeError ? (
        <BsuiAlert variant="danger">
          {strategyCodeError || "Strategy not found or could not be loaded."}
        </BsuiAlert>
      ) : (
        <>
          <div className="strategy-info">
            <div className="strategy-info-content">
              <span
                className="strategy-name deploy-page-strategy-name"
                title={strategy.name}
              >
                {strategy.name}
              </span>
              {linkedStrategyId && (
                <Badge
                  bg="secondary"
                  className="protected-strategy-badge ms-2"
                  title="This is a protected strategy. Code remains with the author."
                >
                  <FontAwesomeIcon icon={faLock} className="me-1" />
                  Protected
                </Badge>
              )}
              {revisionOptions.length > 1 ? (
                <Form.Select
                  aria-label="Choose strategy version"
                  className="strategy-version-select"
                  size="sm"
                  value={aiStrategyRevisionId || ""}
                  onChange={handleRevisionChange}
                >
                  {revisionOptions
                    .slice()
                    .sort(
                      (a, b) => Number(b.version || 0) - Number(a.version || 0),
                    )
                    .map((revision) => (
                      <option
                        key={revision.revisionId || revision.id}
                        value={revision.revisionId || revision.id}
                      >
                        {revision.version != null
                          ? `Version ${revision.version}`
                          : "Revision"}
                      </option>
                    ))}
                </Form.Select>
              ) : (
                <span className="strategy-version">
                  {revisionVersion != null
                    ? `Version: ${revisionVersion}`
                    : "No Revision"}
                </span>
              )}
            </div>
          </div>

          <Form className="mt-3" onSubmit={handleDeploy}>
            <StrategyCompatibility
              assessment={effectiveCompatibilityAssessment}
              action={compatibilityAction}
            />
            {runtimeSecretRequirementItems(runtimeSecretRequirements, compatibilityAction === "backtest" ? "backtest" : "deploy").map((item) => (
              <div className="deploy-runtime-requirement" key={item.key}>
                <strong>{item.title}</strong>
                {item.description && <div>{item.description}</div>}
                {runtimeSecretSetupNotes?.[item.key] && (
                  <div><strong>Setup:</strong> {runtimeSecretSetupNotes[item.key]}</div>
                )}
              </div>
            ))}
            <div
              id="step-1-section"
              className="deploy-page-section broker-step-section"
            >
              <div className="section-step-header">
                <div className="section-step-number">1</div>
                <div className="section-step-title">Link broker</div>
              </div>

              <div className="section-content">
                <BrokerConnectionSelector
                  getApiToken={getApiToken}
                  selectedId={selectedBrokerCredentialId}
                  title="Broker"
                  subtitle="Choose where this bot should trade."
                  actionLabel="Add broker connection"
                  emptyTitle="No broker connections yet. Add one to start this bot."
                  onSelect={(id, item) => {
                    setSelectedBrokerCredentialId(id);
                    setSelectedBrokerCredential(item);
                    if (item?.broker) {
                      setBroker(item.broker);
                      setIsPaper(item.paperOrLive !== "live");
                    }
                    if (id) {
                      pushEvent({
                        event:
                          ANALYTICS_EVENTS.BROKER_CONNECTION_SELECTED_FOR_DEPLOY,
                        brokerCredentialId: id,
                        broker: item?.broker || null,
                        paperOrLive: item?.paperOrLive || null,
                        source: "deploy_page",
                      });
                    }
                  }}
                />

                {selectedBrokerCredentialId && (
                  <div className="deploy-mode-picker">
                    <button
                      type="button"
                      className="deploy-mode-picker__summary"
                      onClick={() =>
                        setDeployModeExpanded((current) => !current)
                      }
                      aria-expanded={deployModeExpanded}
                      aria-controls="deploy-mode-picker-panel"
                    >
                      <span>Run mode</span>
                      <strong>
                        {formatDeployModeSummary(deployMode, scheduleFrequency)}
                      </strong>
                      {deployMode === "auto" && (
                        <Badge
                          bg="secondary"
                          className="deploy-mode-picker__badge"
                        >
                          Recommended
                        </Badge>
                      )}
                      <FontAwesomeIcon
                        icon={deployModeExpanded ? faChevronUp : faChevronDown}
                        aria-hidden="true"
                      />
                    </button>

                    <div
                      id="deploy-mode-picker-panel"
                      className="deploy-mode-picker__panel"
                      hidden={!deployModeExpanded}
                    >
                      <SegmentedControl
                        className="deploy-mode-picker__segmented"
                        label="Run mode"
                        options={DEPLOY_MODE_OPTIONS}
                        value={deployMode}
                        onChange={setDeployMode}
                      />

                      {deployMode === "scheduled" && (
                        <div className="deploy-mode-picker__frequency">
                          <Form.Label htmlFor="deploy-schedule-frequency">
                            Frequency
                          </Form.Label>
                          <Form.Select
                            id="deploy-schedule-frequency"
                            value={scheduleFrequency}
                            onChange={(event) =>
                              setScheduleFrequency(event.target.value)
                            }
                          >
                            {SCHEDULE_FREQUENCY_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      )}

                      <div className="deploy-mode-picker__actions">
                        <BsuiButton
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => setDeployModeExpanded(false)}
                        >
                          Apply
                        </BsuiButton>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div
              id="step-2-section"
              className="deploy-page-section deploy-section"
            >
              <div className="section-step-header">
                <div className="section-step-number">2</div>
                <div className="section-step-title">Start</div>
              </div>

              <div className="section-content">
                {!linkedStrategyId && (
                  <div className="deploy-marketplace-panel">
                    <div className="deploy-marketplace-panel-main">
                      <Form.Check
                        type="checkbox"
                        id="publish-marketplace"
                        className="deploy-marketplace-checkbox"
                        checked={publishToMarketplace}
                        onChange={(event) =>
                          setPublishToMarketplace(event.target.checked)
                        }
                        label={
                          <span>
                            <strong>
                              Share this strategy to the Marketplace
                            </strong>
                          </span>
                        }
                      />
                      <div className="deploy-marketplace-helper">
                        People can discover it, review its backtest results,
                        and start a copy in their own account.
                      </div>
                    </div>

                    {publishToMarketplace && (
                      <div className="deploy-marketplace-pricing">
                        <SegmentedControl
                          className="deploy-marketplace-segmented"
                          label="Marketplace pricing"
                          onChange={setMarketplacePricingModel}
                          options={[
                            { value: "free", label: "Free" },
                            { value: "monthly", label: "Paid monthly" },
                          ]}
                          value={marketplacePricingModel}
                        />

                        <div className="deploy-marketplace-pricing-details">
                          <div
                            className={`deploy-marketplace-price-row ${marketplacePricingModel === "monthly" ? "is-visible" : "is-hidden"}`}
                            aria-hidden={marketplacePricingModel !== "monthly"}
                          >
                            <span className="deploy-marketplace-currency">
                              $
                            </span>
                            <Form.Control
                              type="number"
                              min="1"
                              max="10000"
                              step="1"
                              inputMode="decimal"
                              value={marketplacePriceAmount}
                              onChange={(event) =>
                                setMarketplacePriceAmount(event.target.value)
                              }
                              disabled={marketplacePricingModel !== "monthly"}
                              placeholder="29"
                              aria-label="Monthly Marketplace price"
                            />
                            <span className="deploy-marketplace-period">
                              / month
                            </span>
                          </div>
                          <div
                            className="deploy-marketplace-helper deploy-marketplace-pricing-note"
                            aria-live="polite"
                          >
                            {marketplacePricingModel === "monthly"
                              ? "Paid listings hide the strategy code automatically."
                              : "Free listings are available to copy at no charge."}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {showLiveTradingRequirements && (
                  <div
                    className={`mb-3 live-trading-requirements ${flashAgreements ? "flash-animation" : ""}`}
                  >
                    <div className="mb-3">
                      <strong>Live trading risk</strong>
                    </div>
                    <Form.Check
                      type="checkbox"
                      id="agree-tos"
                      className="mb-2 d-flex align-items-start"
                      label={
                        <span className="live-trading-requirements__copy">
                          I accept the <a href="/terms">Terms of Service</a>
                        </span>
                      }
                      checked={agreeTos}
                      onChange={(e) => setAgreeTos(e.target.checked)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="agree-beta"
                      className="d-flex align-items-start"
                      label={
                        <span className="live-trading-requirements__copy">
                          I understand that live trading uses real money and
                          losses are permanent. Automated trading software,
                          including AI-generated strategies, may produce missed,
                          duplicate, or unintended orders due to bugs, outages,
                          or connectivity failures. AI-generated strategies are
                          not investment advice. I am solely responsible for
                          monitoring my positions, account balances, and all
                          open orders at all times.
                        </span>
                      }
                      checked={agreeBeta}
                      onChange={(e) => setAgreeBeta(e.target.checked)}
                    />
                  </div>
                )}

                <div className="mb-3">
                  {runtimeMissingDetails.length > 0 && (
                    <Alert variant="warning" className="mb-3">
                      This strategy needs saved environment variables before it
                      can start:{" "}
                      {runtimeMissingDetails
                        .map((detail) => detail.message)
                        .join("; ")}
                      .
                    </Alert>
                  )}
                  <SavedEnvVarSetSelector
                    getApiToken={getApiToken}
                    selectedIds={savedEnvVarSetIds}
                    setSelectedIds={setSavedEnvVarSetIds}
                    usageLabel="bot"
                    title="Environment variables"
                    subtitle="Most bots do not need these."
                    defaultExpanded={false}
                    onItemsLoaded={setSavedEnvVarSets}
                  />
                </div>

                <div className="mt-4">
                  <div className="deploy-message-container mb-3">
                    {deployError && (
                      <Alert variant="danger">{deployError}</Alert>
                    )}
                  </div>

                  <div className="deploy-button-container">
                    {startButtonBlocked ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{startButtonDisabledReason}</Tooltip>}
                      >
                        <span className="d-inline-block">
                          <BsuiButton
                            className="deploy-btn-sparkle-large deploy-btn-sparkle-large--blocked"
                            type="submit"
                            aria-disabled="true"
                            title={startButtonDisabledReason}
                            onClick={handleDeployButtonClick}
                          >
                            {startButtonLabel}
                          </BsuiButton>
                        </span>
                      </OverlayTrigger>
                    ) : (
                      <BsuiButton
                        className="deploy-btn-sparkle-large"
                        type="submit"
                        disabled={deploying}
                        loading={deploying}
                        onClick={handleDeployButtonClick}
                      >
                        {deploying ? "Starting..." : startButtonLabel}
                      </BsuiButton>
                    )}
                  </div>
                  {isDeployDisabled && !deploying && (
                    <div className="deploy-page-disabled-reason mt-2 text-danger small text-center">
                      {startButtonDisabledReason}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Form>
        </>
      )}
      {!loading && strategy && performanceData && (
        <div className="deploy-page-performance-bottom mt-4">
          <div className="deploy-container deploy-page-full-width">
            <div className="performance-toggle-section">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowPerformance(!showPerformance)}
                className="performance-toggle-btn"
                aria-expanded={showPerformance}
              >
                <FontAwesomeIcon
                  icon={showPerformance ? faChevronUp : faChevronDown}
                  className="me-2"
                />
                {showPerformance ? "Hide" : "View"} Strategy Performance
              </Button>
            </div>

            <Collapse in={showPerformance}>
              <div className="performance-collapse-content">
                <BacktestResults
                  performanceData={performanceData}
                  title="Strategy Performance"
                  showTitle={true}
                  compact={false}
                />
              </div>
            </Collapse>
          </div>
        </div>
      )}

      <PaywallModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        limitType={paywallData?.limitType}
        usage={paywallData?.usage}
        activeDeployments={paywallData?.activeDeployments}
        checkoutUrl={paywallData?.checkoutUrl}
      />
    </PageShell>
  );
}
